/**
 * Page route fallback.
 *
 * Explains routing behavior, guards, and response semantics.
 */
import { DEFAULT_SHOP_LOCALE } from '../../../../i18n/shopLocale'
import { enrichStorefrontPageRuntimeBlocks } from '../../../../cms/storefrontRuntime'
import { buildCategoryChildrenTree, normalizePage } from '../../../normalizers'
import { buildCanonicalProductPath, buildCategoryPathFromTrail, resolveCategoryFromPath } from '../../../routing'
import { shouldFallbackToDefaultLocale } from '../../../seo/localePolicy'
import { fetchCatalogCategories, fetchCategoryProducts, fetchProductBySlug } from '../catalogApi'
import { attachPageSeoAlternates, fetchPageResponse } from '../routeContentLoader.pageSeo'
import { replaceLocalizedPathIfNeeded } from './navigation'
import type { LoadedRouteContentData, PageRoute, RouteContentLoaderLocaleContext } from './types'

export const loadFallbackCategoryOrProduct = async ({
  route,
  signal,
  localeContext,
}: {
  route: Extract<PageRoute, { mode: 'page' }>
  signal: AbortSignal
  localeContext: RouteContentLoaderLocaleContext
}): Promise<LoadedRouteContentData> => {
  const categories = await fetchCatalogCategories(signal).catch(() => [])
  const categoryMatch = resolveCategoryFromPath(categories, route.slug)

  if (categoryMatch) {
    const children = buildCategoryChildrenTree(categories, categoryMatch.category._id)
    const primaryCategoryId = categoryMatch.category.parentCategoryId ? categoryMatch.category._id : undefined
    // Top-level categories remain aggregate hubs. Nested category pages use
    // their own primary category as the exact merchandising scope.
    const initialProductsPage = await fetchCategoryProducts(categoryMatch.category._id, signal, {
      page: 1,
      limit: 9,
      primaryCategoryId,
    }).catch(() => null)
    const categoryProductsEntries = await Promise.all(
      children.map(async (child) => {
        try {
          const productsPage = await fetchCategoryProducts(child._id, signal, {
            page: 1,
            limit: 9,
            primaryCategoryId: child._id,
          })
          return [child._id, { products: productsPage.products, pagination: productsPage.pagination }] as const
        } catch {
          return null
        }
      }),
    )
    const categoryProducts = Object.fromEntries(categoryProductsEntries.filter((entry): entry is NonNullable<typeof entry> => Boolean(entry)))
    const products = initialProductsPage?.products ?? []
    const canonicalPath = buildCategoryPathFromTrail(categoryMatch.trail)
    replaceLocalizedPathIfNeeded({
      nextPath: canonicalPath,
      toLocalizedCanonical: localeContext.toLocalizedCanonical,
    })

    return {
      type: 'category',
      categoryData: {
        category: categoryMatch.category,
        trail: categoryMatch.trail,
        children,
        products,
        productsPagination: initialProductsPage?.pagination,
        categoryProducts,
      },
    }
  }

  const product = await fetchProductBySlug(route.slug, signal)
  const canonicalPath = buildCanonicalProductPath(product, categories)
  replaceLocalizedPathIfNeeded({
    nextPath: canonicalPath,
    toLocalizedCanonical: localeContext.toLocalizedCanonical,
  })

  return { type: 'product', product }
}

export const loadFallbackDefaultLocalePage = async ({
  route,
  signal,
  requestedLocale,
}: {
  route: Extract<PageRoute, { mode: 'page' }>
  signal: AbortSignal
  requestedLocale: string
}): Promise<LoadedRouteContentData | null> => {
  if (!shouldFallbackToDefaultLocale({ requestedLocale, responseStatus: 404 })) return null

  const fallbackResponse = await fetchPageResponse({
    pageIdOrSlug: route.slug,
    locale: DEFAULT_SHOP_LOCALE,
    signal,
  })
  if (!fallbackResponse.ok) return null

  const fallbackData = (await fallbackResponse.json()) as { page?: Record<string, unknown> }
  if (!fallbackData.page || typeof fallbackData.page !== 'object' || Array.isArray(fallbackData.page)) {
    return null
  }

  const fallbackPage = normalizePage(fallbackData.page)
  fallbackPage.seoCanonicalLocale = DEFAULT_SHOP_LOCALE
  fallbackPage.seoFallbackFromLocale = requestedLocale
  await attachPageSeoAlternates({ page: fallbackPage, signal })
  const runtimePage = await enrichStorefrontPageRuntimeBlocks({
    page: fallbackPage,
    context: {
      locale: DEFAULT_SHOP_LOCALE,
      pageId: fallbackPage._id,
      pageSlug: fallbackPage.slug,
    },
  })
  return { type: 'page', page: runtimePage }
}
